<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title class="mb-4">
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="headline font-weight-bold">Orden de Salida</span>
        </v-card-title>

        <!-- Estadísticas -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6" md="3">
            <v-card class="text-center pa-3">
              <div class="display-1 font-weight-bold primary--text">{{ totalOutings }}</div>
              <div class="caption grey--text">Salidas Totales</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="text-center pa-3">
              <div class="display-1 font-weight-bold green--text">{{ completedOutings }}</div>
              <div class="caption grey--text">Completadas</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="text-center pa-3">
              <div class="display-1 font-weight-bold orange--text">{{ inProgressOutings }}</div>
              <div class="caption grey--text">En Juego</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="text-center pa-3">
              <div class="display-1 font-weight-bold info--text">{{ pendingOutings }}</div>
              <div class="caption grey--text">Por Salir</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tabla Orden de Salida -->
        <v-card>
          <v-card-subtitle class="pa-4">
            <v-icon small class="mr-2">mdi-table-large</v-icon>
            {{ todayDate }}
          </v-card-subtitle>
          <v-data-table
            :headers="headers"
            :items="todaysOutings"
            class="elevation-0"
            :no-data-text="'Sin salidas hoy'"
            sort-by="scheduled_time">

            <template v-slot:item.group_number="{ item }">
              <v-chip color="primary" text-color="white" small>
                Grupo {{ item.group_number }}
              </v-chip>
            </template>

            <template v-slot:item.players="{ item }">
              <div>
                <v-chip v-for="(player, index) in item.players" :key="index" x-small class="mr-1 mb-1">
                  {{ player }}
                </v-chip>
              </div>
            </template>

            <template v-slot:item.scheduled_time="{ item }">
              <div class="font-weight-bold">{{ item.scheduled_time }}</div>
              <div v-if="item.actual_start_time" class="caption grey--text">
                Inicio: {{ item.actual_start_time }}
              </div>
            </template>

            <template v-slot:item.current_hole="{ item }">
              <div class="text-center">
                <v-chip color="info" text-color="white" small>
                  Hoyo {{ item.current_hole }}
                </v-chip>
              </div>
            </template>

            <template v-slot:item.pace_status="{ item }">
              <v-chip :color="getPaceStatusColor(item.pace_status)" small>
                {{ getPaceStatusText(item.pace_status) }}
              </v-chip>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip :color="getStatusColor(item.status)" text-color="white" small>
                {{ getStatusLabel(item.status) }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small class="mr-2" v-bind="attrs" v-on="on" @click="openMarkDialog(item)">
                    <v-icon small color="orange">mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Marcar hoyo</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" @click="openWarningDialog(item)">
                    <v-icon small color="red">mdi-alert-circle</v-icon>
                  </v-btn>
                </template>
                <span>Registrar amonestación</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Diálogo para marcar hoyo -->
    <v-dialog v-model="markDialog" max-width="500px" persistent>
      <v-card v-if="selectedOuting">
        <v-card-title>Marcar Hoyo - Grupo {{ selectedOuting.group_number }}</v-card-title>
        <v-card-text>
          <v-form ref="markForm" v-model="validMark" lazy-validation>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model.number="selectedOuting.current_hole"
                  label="Hoyo Actual"
                  type="number"
                  outlined
                  dense
                  min="1"
                  max="18"
                  :rules="[v => (v >= 1 && v <= 18) || 'Hoyo debe estar entre 1 y 18']"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="selectedOuting.pace_status"
                  :items="[
                    { text: 'En Ritmo', value: 'on_pace' },
                    { text: 'Ligeramente Atrasado', value: 'slightly_behind' },
                    { text: 'Muy Atrasado', value: 'behind_schedule' }
                  ]"
                  label="Estado de Ritmo"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="markDialog = false">Cancelar</v-btn>
          <v-btn color="primary" text @click="updateOutingHole" :disabled="!validMark">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo para advertencia (amonestación) -->
    <WarningDialog
      :dialog.sync="warningDialog"
      :group="selectedOuting"
      @save="recordWarning"
    />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import WarningDialog from './components/WarningDialog.vue'
import { marshallOutingsApi } from '@/services/marshall'

export default {
  name: 'MarshallStarterOrder',
  components: {
    WarningDialog
  },
  data () {
    return {
      markDialog: false,
      warningDialog: false,
      validMark: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      selectedOuting: null,
      headers: [
        { text: 'Grupo', value: 'group_number', width: '10%' },
        { text: 'Jugadores', value: 'players', width: '30%', sortable: false },
        { text: 'Horario', value: 'scheduled_time', width: '12%' },
        { text: 'Hoyo', value: 'current_hole', width: '10%' },
        { text: 'Ritmo', value: 'pace_status', width: '12%' },
        { text: 'Estado', value: 'status', width: '12%' },
        { text: 'Acciones', value: 'actions', width: '14%', sortable: false, align: 'center' }
      ],
      outings: [],
      loading: false
    }
  },
  created () {
    this.fetchOutings()
  },
  computed: {
    todayDate () {
      const today = new Date()
      return today.toLocaleDateString('es-MX', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    todaysOutings () {
      const today = new Date().toISOString().split('T')[0]
      return this.outings.filter(o => o.date === today)
    },
    totalOutings () {
      return this.outings.length
    },
    completedOutings () {
      return this.outings.filter(o => o.status === 'completado').length
    },
    inProgressOutings () {
      return this.outings.filter(o => o.status === 'en_juego').length
    },
    pendingOutings () {
      return this.outings.filter(o => o.status === 'por_salir').length
    }
  },
  methods: {
    fetchOutings () {
      this.loading = true
      marshallOutingsApi.getOutings()
        .then(response => {
          this.outings = response.data.results || response.data
        })
        .catch(error => {
          console.error('Error cargando salidas:', error)
          this.showSnackbar('Error al cargar las salidas', 'error')
        })
        .finally(() => {
          this.loading = false
        })
    },

    getStatusColor (status) {
      const colors = {
        por_salir: 'grey',
        en_juego: 'blue',
        completado: 'green'
      }
      return colors[status] || 'grey'
    },

    getStatusLabel (status) {
      const labels = {
        por_salir: 'Por Salir',
        en_juego: 'En Juego',
        completado: 'Completado'
      }
      return labels[status] || 'Desconocido'
    },

    getPaceStatusColor (status) {
      const colors = {
        on_pace: 'green',
        slightly_behind: 'orange',
        behind_schedule: 'red'
      }
      return colors[status] || 'grey'
    },

    getPaceStatusText (status) {
      const texts = {
        on_pace: 'En Ritmo',
        slightly_behind: 'Ligeramente Atrasado',
        behind_schedule: 'Muy Atrasado'
      }
      return texts[status] || 'Desconocido'
    },

    openMarkDialog (outing) {
      this.selectedOuting = { ...outing }
      this.markDialog = true
    },

    updateOutingHole () {
      if (this.$refs.markForm.validate()) {
        const data = {
          current_hole: this.selectedOuting.current_hole,
          pace_status: this.selectedOuting.pace_status
        }

        if (!this.selectedOuting.actual_start_time) {
          data.actual_start_time = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
        }

        marshallOutingsApi.updateOuting(this.selectedOuting.id, data)
          .then(response => {
            const index = this.outings.findIndex(o => o.id === this.selectedOuting.id)
            if (index > -1) {
              this.outings[index] = response.data
            }
            this.showSnackbar(`Grupo ${this.selectedOuting.group_number} marcado en hoyo ${this.selectedOuting.current_hole}`, 'success')
            this.markDialog = false
          })
          .catch(error => {
            console.error('Error actualizando salida:', error)
            this.showSnackbar('Error al actualizar la salida', 'error')
          })
      }
    },

    openWarningDialog (outing) {
      this.selectedOuting = outing
      this.warningDialog = true
    },

    recordWarning (warning) {
      this.showSnackbar(`Amonestación registrada: ${warning.type}`, 'success')
      this.warningDialog = false
      this.fetchOutings()
    },

    showSnackbar (message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
</style>
